import {
  FCurveInterpolation,
  type FCurveMapping,
  type FCurvePoint,
} from "../blend-reader/types/BlendDriver.ts";

/**
 * Evaluates an FCurve mapping at a given input value.
 * Supports CONSTANT, LINEAR, and BEZIER interpolation with handle types.
 *
 * Reference: docs/02-architecture.md "FCurve Evaluation" section
 */
export function evaluateFCurve(curve: FCurveMapping, x: number): number {
  const { points } = curve;
  if (points.length === 0) return x;

  // Clamp to curve bounds
  if (x <= points[0].co[0]) return points[0].co[1];
  if (x >= points[points.length - 1].co[0])
    return points[points.length - 1].co[1];

  // Find the segment containing x
  let segIdx = 0;
  for (let i = points.length - 1; i >= 0; i--) {
    if (points[i].co[0] <= x) {
      segIdx = i;
      break;
    }
  }

  const p0 = points[segIdx];
  const p1 = points[segIdx + 1];

  switch (p0.interpolation) {
    case FCurveInterpolation.Constant:
      return p0.co[1];

    case FCurveInterpolation.Linear: {
      const t = (x - p0.co[0]) / (p1.co[0] - p0.co[0]);
      return p0.co[1] + t * (p1.co[1] - p0.co[1]);
    }

    case FCurveInterpolation.Bezier:
      return evaluateBezierSegment(p0, p1, x);
  }
}

/**
 * Evaluate a cubic bezier segment to find y at target x.
 * Uses Newton-Raphson iteration to find the t parameter for the given x,
 * then evaluates the curve at that t for y.
 */
function evaluateBezierSegment(
  p0: FCurvePoint,
  p1: FCurvePoint,
  targetX: number,
): number {
  // Control points for the cubic bezier in x and y:
  // P0 = p0.co, P1 = p0.rightHandle, P2 = p1.leftHandle, P3 = p1.co
  const x0 = p0.co[0];
  const y0 = p0.co[1];
  const x1 = p0.rightHandle[0];
  const y1 = p0.rightHandle[1];
  const x2 = p1.leftHandle[0];
  const y2 = p1.leftHandle[1];
  const x3 = p1.co[0];
  const y3 = p1.co[1];

  // Find t for the target x using Newton-Raphson
  let t = (targetX - x0) / (x3 - x0); // initial guess

  for (let iter = 0; iter < 16; iter++) {
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;

    // Evaluate x(t)
    const xt = mt3 * x0 + 3 * mt2 * t * x1 + 3 * mt * t2 * x2 + t3 * x3;
    const err = xt - targetX;

    if (Math.abs(err) < 1e-6) break;

    // Derivative dx/dt
    const dxt =
      3 * mt2 * (x1 - x0) + 6 * mt * t * (x2 - x1) + 3 * t2 * (x3 - x2);
    if (Math.abs(dxt) < 1e-10) break;

    t -= err / dxt;
    t = Math.max(0, Math.min(1, t));
  }

  // Evaluate y(t)
  const mt = 1 - t;
  const mt2 = mt * mt;
  const mt3 = mt2 * mt;
  const t2 = t * t;
  const t3 = t2 * t;

  return mt3 * y0 + 3 * mt2 * t * y1 + 3 * mt * t2 * y2 + t3 * y3;
}
